/**
 * Version history (R3, screen 23): every publish is a numbered snapshot,
 * nameable in place, and restorable INTO THE DRAFT — restore loads the old
 * definition as unsaved changes, never straight to the live form, so the
 * owner reviews what came back (and can test it) before publishing it
 * forward, the same shape as submission history's restore-through-the-editor.
 *
 * The one exception is the document. It never rides the draft — it saves
 * live — so a version's PDF is put back at once, and the confirm says so
 * before asking (`has_document` on the row is what decides the sentence).
 *
 * `VersionList` is the list itself: the whole of it on Settings › Versions
 * (`FormVersionsPage`, the `…/versions` route), and the newest few inside
 * the Publish panel. Both restore through the workspace's own handler.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FormVersionSummary, listFormVersions, renameFormVersion } from '../../api/forms';
import { useFormWorkspace } from './FormWorkspace';

export function VersionList({ formId, compact, limit, busy, onRestore, editing }: {
  formId: number;
  /** the Publish panel's short form: no head row, no rename */
  compact?: boolean;
  /** show only the newest `limit` versions */
  limit?: number;
  busy?: boolean;
  /** Load this version into the working draft (its document at once). */
  onRestore: (version: number) => void;
  /**
   * The draft in the editor, when there is one: drawn as the top row,
   * "v{current+1} · Draft · unpublished changes … Editing" (screen 23).
   */
  editing?: { unpublished: boolean };
}) {
  const [versions, setVersions] = useState<FormVersionSummary[] | null>(null);
  const [current, setCurrent] = useState(0);
  const [error, setError] = useState('');
  const [renaming, setRenaming] = useState<number | null>(null);
  const [nameDraft, setNameDraft] = useState('');
  const [confirming, setConfirming] = useState<number | null>(null);

  useEffect(() => {
    let live = true;
    listFormVersions(formId)
      .then((r) => { if (live) { setVersions(r.versions); setCurrent(r.current); } })
      .catch((e) => { if (live) setError(e.message || 'Could not load versions.'); });
    return () => { live = false; };
  }, [formId]);

  async function commitName(v: FormVersionSummary) {
    const name = nameDraft.trim();
    setRenaming(null);
    if (name === (v.name || '')) return;
    try {
      const r = await renameFormVersion(formId, v.version, name);
      setVersions((list) => (list || []).map((x) => (x.version === v.version ? { ...x, name: r.name } : x)));
    } catch (e: any) {
      setError(e.message || 'Rename failed.');
    }
  }

  const shown = versions ? (limit ? versions.slice(0, limit) : versions) : null;
  const when = (iso: string) => new Date(iso).toLocaleString(undefined, compact
    ? { dateStyle: 'medium' } : { dateStyle: 'medium', timeStyle: 'short' });

  return (
    <div className={`fv-list${compact ? ' compact' : ''}`}>
      {error && <div className="ad-err">{error}</div>}
      {!shown ? (
        <div className="fs-cell-sub" style={{ padding: '8px 0' }}>Loading…</div>
      ) : shown.length === 0 && !editing ? (
        <div className="fs-cell-sub" style={{ padding: '8px 0' }}>No versions yet — the first publish will create version 1.</div>
      ) : (
        <table className={`fs-table fv-table${compact ? '' : ' stack'}`} style={{ width: '100%' }}>
          {!compact && (
            <thead>
              <tr><th>Version</th><th>Name</th><th>Published</th><th /></tr>
            </thead>
          )}
          <tbody>
            {editing && !compact && (
              <tr className="fv-editing">
                <td data-label="Version" style={{ whiteSpace: 'nowrap' }}>v{current + 1}</td>
                <td data-label="Name">
                  <span className="fv-name">Draft{editing.unpublished ? ' · unpublished changes' : ''}</span>
                  <div className="fs-cell-sub">You</div>
                </td>
                <td data-label="Published" style={{ whiteSpace: 'nowrap' }}>Now</td>
                <td className="fv-act" style={{ textAlign: 'right' }}><span className="fs-cell-sub faint">Editing</span></td>
              </tr>
            )}
            {shown.map((v) => (
              <tr key={v.id}>
                <td data-label="Version" style={{ whiteSpace: 'nowrap' }}>
                  v{v.version}
                </td>
                <td data-label="Name">
                  {compact ? (
                    <span className="fv-name">{v.name || <span className="fs-cell-sub faint">{v.published_by_email || 'Published'}</span>}</span>
                  ) : renaming === v.version ? (
                    <input
                      autoFocus className="fs-input" value={nameDraft} maxLength={80} aria-label={`Name for v${v.version}`}
                      onChange={(e) => setNameDraft(e.target.value)}
                      onBlur={() => void commitName(v)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') void commitName(v);
                        if (e.key === 'Escape') setRenaming(null);
                      }}
                    />
                  ) : (
                    <button
                      type="button" className="fb-version-name" title="Rename this version"
                      onClick={() => { setRenaming(v.version); setNameDraft(v.name || ''); }}
                    >
                      {v.name || <span className="fs-cell-sub faint">Add a name…</span>}
                    </button>
                  )}
                  {!compact && v.published_by_email && <div className="fs-cell-sub">{v.published_by_email}</div>}
                </td>
                <td data-label="Published" style={{ whiteSpace: 'nowrap' }}>{when(v.created_at)}</td>
                <td className="fv-act" style={{ textAlign: 'right' }}>
                  {v.version === current ? (
                    <span className="fv-live" title="Respondents see this version">Live</span>
                  ) : confirming === v.version ? (
                    <span className="fv-confirm">
                      <span className="fs-cell-sub fv-confirm-text">
                        Loads v{v.version} into your draft as unsaved changes.
                        {v.has_document ? ' Its PDF document goes live immediately.' : ''}
                      </span>
                      <button className="fs-btn primary sm" disabled={busy} onClick={() => { setConfirming(null); onRestore(v.version); }}>
                        Replace my changes
                      </button>
                      <button className="fs-btn neutral sm" disabled={busy} onClick={() => setConfirming(null)}>Cancel</button>
                    </span>
                  ) : (
                    <button className="fs-btn text sm fv-restore" disabled={busy} onClick={() => setConfirming(v.version)}>
                      Restore
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

/** Settings › Versions — the `…/versions` route (§2). */
export default function FormVersionsPage() {
  const { form, state, busy, restoreVersion } = useFormWorkspace();
  const navigate = useNavigate();
  return (
    <div className="fv-page">
      <div className="fs-page-head">
        <div>
          <h1>Versions</h1>
          <div className="fs-page-sub">
            Every publish is saved. Restoring puts an older version back in the editor as a draft, so nothing changes
            until you publish it — except its PDF document, which never rides the draft and comes back at once.
          </div>
        </div>
      </div>
      <div className="fs-card pad">
        <VersionList
          formId={form.id} busy={busy}
          editing={state !== 'clean' ? { unpublished: true } : undefined}
          onRestore={(v) => { void restoreVersion(v).then(() => navigate(`/admin/forms/${form.id}/build`)); }}
        />
      </div>
    </div>
  );
}
